<template>
  <ul class="lists-section px-0 mb-0" v-if="starredItems.length > 0">
    <li
      class="caret-link nav-toplevel nav-toplevel-item nav-item position-relative pointer d-flex align-items-center"
      @click="toggleTeamsVisibility"
    >
      <collapse-caret
        class="flex-shrink-0 collapse-section"
        :size="16"
        :container-to-icon-ratio="0.5"
        :expanded="isExpanded"
      />
      <div class="nav-top-link">
        <i class="starred-icon"></i>
        <span class="py-1">Starred</span>
      </div>
    </li>
    <b-collapse v-model="isExpanded" class="lists">
      <ul class="nav flex-column px-0" :key="listKey" ref="listContainer">
        <template v-for="item in starredItems">
          <team-link
            class="list-link"
            :show-gripper="allowDragDrop"
            :data-id="item.starredId"
            :key="item.starredId"
            :team="item"
            :show-icon="isProjectsEnabled"
            v-if="item.contextType === CONTEXT_TYPE.TEAM"
          />
          <project-link
            class="list-link"
            :show-gripper="allowDragDrop"
            :data-id="item.starredId"
            :key="item.starredId"
            :project="item"
            v-else-if="isProjectsEnabled && item.contextType === CONTEXT_TYPE.PROJECT"
          />
        </template>
      </ul>
    </b-collapse>
  </ul>
</template>

<script>
import CollapseCaret from '@/components/CollapseCaret'
import ProjectLink from '@/components/project/ProjectLink.vue'
import TeamLink from '@/components/sidebar/TeamLink'
import { CONTEXT_TYPE } from '@/constants/common'
import { featureFlags } from '@/constants/featureFlags'
import { ROUTE_NAMES } from '@/constants/routes'
import { STORAGE_KEYS } from '@/constants/storage'
import DragAndDropMixin from '@/mixins/DragAndDropMixin'
import EventBusMixin from '@/mixins/EventBusMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import MediaQueryMixin from '@/mixins/MediaQueryMixin'
import UserListsMixin from '@/mixins/UserListsMixin'
import SidebarStorageMixins from '@/mixins/SidebarStorageMixins'
import { BCollapse } from 'bootstrap-vue'
import { mapActions, mapGetters } from 'vuex'

/**
 * This shows sidebar links of starred teams/projects
 */
export default {
  name: 'StarredLinks',
  props: ['orgId', 'subOrgId'],
  mixins: [
    EventBusMixin,
    UserListsMixin,
    SidebarStorageMixins,
    DragAndDropMixin,
    MediaQueryMixin,
    FeaturesMixin
  ],
  components: {
    ProjectLink,
    CollapseCaret,
    BCollapse,
    TeamLink
  },
  data() {
    return {
      ROUTE_NAMES,
      CONTEXT_TYPE,
      isExpanded: this.getSidebarFlag(STORAGE_KEYS.sidebar.flags.starredTeamsExpanded),
      initiativeLists: [],
      activeList: null,
      listKey: 1
    }
  },
  computed: {
    ...mapGetters('starred', ['starred']),
    ...mapGetters('project', ['projectById']),
    isProjectsEnabled() {
      return !!this.getFeatureFlagValue(this.orgId, featureFlags.ALLOW_PROJECTS)
    },
    allowDragDrop() {
      return !this.isMobileMode
    },
    orgTeams() {
      return this.$store.getters.userTeamsByOrgId(this.orgId)
    },
    starredItems() {
      return this.starred?.map(this.enrichStarredItem).filter(Boolean) || []
    },
    starredProjects() {
      return (
        this.starred
          ?.filter(x => x.contextType === CONTEXT_TYPE.PROJECT)
          .map(x => ({
            id: x.contextId,
            starredId: x.id,
            ...this.projectById(x.contextId)
          })) || []
      )
    }
  },
  methods: {
    ...mapActions(['changeRank']),
    ...mapActions('project', ['getProject']),
    enrichStarredItem(item) {
      const starredId = item.id
      let enriched = {
        contextType: item.contextType,
        starredId
      }
      if (item.contextType === CONTEXT_TYPE.TEAM) {
        enriched = {
          ...enriched,
          contextType: item.contextType,
          ...this.orgTeams.find(team => team.id === item.contextId)
        }
      } else if (item.contextType === CONTEXT_TYPE.PROJECT) {
        enriched = {
          ...enriched,
          contextType: item.contextType,
          ...this.projectById(item.contextId)
        }
      }
      return enriched
    },
    toggleTeamsVisibility() {
      this.isExpanded = !this.isExpanded
      this.setSidebarFlag(STORAGE_KEYS.sidebar.flags.starredTeamsExpanded, this.isExpanded)
    },
    async onDrop({ afterId, beforeId, subjectId }) {
      try {
        await this.changeRank({
          orgId: this.orgId,
          type: 'starred',
          subjectId,
          beforeId,
          afterId
        })
      } catch {
        this.listKey++
        this.enableListDragDrop()
      }
    },
    enableListDragDrop() {
      this.$nextTick(() => {
        if (this.allowDragDrop && this.starredItems.length > 0) {
          this.enableDragAndDrop({
            containers: [this.$refs.listContainer],
            onDrop: this.onDrop,
            isFullyDraggable: true
          })
        }
      })
    },
    fetchMissingProjects() {
      this.starredProjects.forEach(x => {
        if (!x.title) {
          this.getProject({ projectId: x.id, orgId: this.orgId })
        }
      })
    }
  },
  mounted() {
    this.fetchMissingProjects()
  },
  watch: {
    starredItems: 'enableListDragDrop',
    starred: 'fetchMissingProjects',
    allowDragDrop: 'enableListDragDrop'
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_hoverStates.scss';
@import '~@/styles/_styleConfigurations.scss';

.starred-icon {
  @include dimension(16px, 16px);
}
.collapse-section {
  opacity: 0;
}
.caret-link:hover .collapse-section {
  opacity: 1;
}
</style>
